import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart, faEye, faMapMarkerAlt, faChain, faLocationDot } from '@fortawesome/free-solid-svg-icons';
import { getNoteImpact, fetchNote } from '../../services/notesService';
import { Note, NoteImpact } from '../../types/interfaces';
import HeartButton from '../../components/common/HeartButton';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';

/**
 * Page displaying a note's impact metrics including:
 * - View counts
 * - Heart counts
 * - QR scans
 * - Inspired notes
 * - Display locations
 */
const NoteImpactPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [note, setNote] = useState<Note | null>(null);
  const [impact, setImpact] = useState<NoteImpact | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [noteData, impactData] = await Promise.all([
          fetchNote(id as string),
          getNoteImpact(id as string)
        ]);
        setNote(noteData);
        setImpact(impactData);
      } catch (err) {
        console.error('Error fetching note impact data:', err);
        setError('Unable to load note impact data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  // Handle heart update from the HeartButton component
  const handleHeartUpdate = (heartCount: number) => {
    if (note) {
      setNote({
        ...note,
        heart_count: heartCount
      });
    }
  };

  if (loading) {
    return <LoadingSpinner fullPage text="Loading note impact data..." />;
  }

  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">{error}</div>
      </div>
    );
  }

  if (!note || !impact) {
    return (
      <div className="container mt-4">
        <div className="alert alert-warning">Note not found or you don't have permission to view it.</div>
      </div>
    );
  }

  return (
    <div className="container my-4">
      <PageHeader title="Note Impact" subtitle="See how your note is making a difference" />
      
      <div className="row mb-4">
        <div className="col">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Your Note</h5>
            </div>
            <div className="card-body">
              <blockquote className="blockquote mb-0">
                <p>{note.note}</p>
                <footer className="blockquote-footer">
                  Written on {new Date(note.createdAt).toLocaleDateString()}
                  {note.location?.city && note.location?.state && (
                    <>
                      {' '}from{' '}
                      <cite title="Location">
                        {note.location.city}, {note.location.state}
                      </cite>
                    </>
                  )}
                </footer>
              </blockquote>
              <div className="mt-3">
                <HeartButton 
                  noteId={note._id} 
                  initialHeartCount={note.heart_count}
                  onHeartUpdated={(heartCount) => handleHeartUpdate(heartCount)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="row mb-4 metrics-mobile-stack">
        <div className="col-md-3 col-sm-6">
          <div className="card text-center h-100">
            <div className="card-body">
              <FontAwesomeIcon icon={faEye} size="2x" className="text-primary mb-2" />
              <h5 className="card-title">{impact.totalViews}</h5>
              <p className="card-text">Total Views</p>
            </div>
          </div>
        </div>
        <div className="col-md-3 col-sm-6">
          <div className="card text-center h-100">
            <div className="card-body">
              <FontAwesomeIcon icon={faHeart} size="2x" className="text-danger mb-2" />
              <h5 className="card-title">{impact.totalHearts}</h5>
              <p className="card-text">Hearts</p>
            </div>
          </div>
        </div>
        <div className="col-md-3 col-sm-6">
          <div className="card text-center h-100">
            <div className="card-body">
              <FontAwesomeIcon icon={faMapMarkerAlt} size="2x" className="text-success mb-2" />
              <h5 className="card-title">{impact.totalQrScans}</h5>
              <p className="card-text">QR Scans</p>
            </div>
          </div>
        </div>
        <div className="col-md-3 col-sm-6">
          <div className="card text-center h-100">
            <div className="card-body">
              <FontAwesomeIcon icon={faChain} size="2x" className="text-info mb-2" />
              <h5 className="card-title">{impact.inspiredNotesCount}</h5>
              <p className="card-text">Inspired Notes</p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="row mb-4">
        <div className="col-lg-6 mb-4">
          <div className="card h-100">
            <div className="card-header">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faLocationDot} className="me-2" />
                Display Locations
              </h5>
            </div>
            <div className="card-body">
              {impact.displayLocationsSummary.length === 0 ? (
                <div className="alert alert-info">
                  Your note hasn't been displayed yet. Check back soon!
                </div>
              ) : (
                <ul className="list-group list-group-flush">
                  {impact.displayLocationsSummary.map((location, index) => (
                    <li key={index} className="list-group-item">
                      {location.city}, {location.state}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="card-footer text-muted">
              Displayed on {impact.uniqueDisplaysCount} different displays
            </div>
          </div>
        </div>
        <div className="col-lg-6 mb-4">
          <div className="card h-100">
            <div className="card-header">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faChain} className="me-2" />
                Inspiration Chain
              </h5>
            </div>
            <div className="card-body">
              <div className="alert alert-info">
                Your note is part of an inspiration chain {impact.inspirationChainLength} notes long.
              </div>
              {impact.inspiredNotesCount > 0 ? (
                <p>
                  Your note has directly inspired {impact.inspiredNotesCount} new notes from 
                  other people. This is how you are making a difference!
                </p>
              ) : (
                <p>
                  Your note hasn't inspired any new notes yet, but it will as more people see it!
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <div className="text-center mt-3">
        <Link to="/my-notes" className="btn btn-outline-primary">
          Back to My Notes
        </Link>
      </div>
    </div>
  );
};

export default NoteImpactPage; 